import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Checkbox, Form, Input, Modal, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import { EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import CompactTable from './CompactTable';
import { ok, runAction } from './utils';

const { Text } = Typography;
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };

/** Reverse DNS (PTR) of the VM's public addresses (RdnsService). Hidden when no address offers it. */
const RdnsCard = ({ id, api, isMobile, disabled, isAdmin }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(null); // { address?, prefix?, ptr }
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        if (!api.getVmRdns) return;
        setLoading(true);
        try {
            const res = await api.getVmRdns(id);
            if (ok(res)) setItems(res.data?.items || []);
        } finally {
            setLoading(false);
        }
    }, [api, id]);

    useEffect(() => { load(); }, [load]);

    const offered = items.filter((r) => r.mode === 'zone' || r.mode === 'manual');
    if (offered.length === 0) return null;

    const open = (r) => {
        setEdit(r);
        form.setFieldsValue({ address: r.address || '', ptr: r.ptr || '', skip_check: false });
    };

    const save = async (values) => {
        setSaving(true);
        const res = await runAction(() => api.setVmRdns(id, { address: values.address, ptr: values.ptr || '', skip_check: values.skip_check ? 1 : 0 }),
            t('Saved'), t('Save failed'));
        setSaving(false);
        if (res) { setEdit(null); load(); }
    };

    const reset = async (r) => {
        const res = await runAction(() => api.setVmRdns(id, { address: r.address, ptr: '' }), t('Saved'), t('Save failed'));
        if (res) load();
    };

    const statusTag = (r) => {
        if (r.status === 'pending') return <Tooltip title={t('Set by the upstream network; usually within a few hours')}><Tag color="processing">{t('Pending')}</Tag></Tooltip>;
        if (r.status === 'failed') return <Tooltip title={r.error || ''}><Tag color="error">{t('Failed')}</Tag></Tooltip>;
        if (r.status === 'active') return <Tag color="success">{t('Active')}</Tag>;
        return null;
    };

    const columns = [
        { title: t('Address'), key: 'address', render: (_, r) => <Text style={mono}>{r.address || r.prefix}</Text> },
        {
            title: 'PTR',
            key: 'ptr',
            render: (_, r) => (r.address
                ? <Space size={6} wrap><Text style={mono}>{r.ptr || <Text type="secondary">{t('Default')}</Text>}</Text>{statusTag(r)}</Space>
                : <Text type="secondary">{t('Any address in this prefix')}</Text>),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, r) => (r.address ? (
                <Space size={4}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => open(r)} disabled={disabled} aria-label={t('Edit')} />
                    {r.ptr && (
                        <Popconfirm title={t('Restore the default PTR?')} onConfirm={() => reset(r)} disabled={disabled}>
                            <Button size="small" type="text" icon={<UndoOutlined />} disabled={disabled} aria-label={t('Restore default')} />
                        </Popconfirm>
                    )}
                </Space>
            ) : (
                <Button size="small" icon={<PlusOutlined />} onClick={() => open(r)} disabled={disabled}>{t('Add')}</Button>
            )),
        },
    ];

    return (
        <Card size="small" title={<Tooltip title={t('The hostname must resolve to the address first (A / AAAA record)')}>{t('Reverse DNS (PTR)')}</Tooltip>}>
            <CompactTable isMobile={isMobile} columns={columns} dataSource={offered} loading={loading} rowKey={(r) => r.address || r.prefix} />
            <Modal
                title={t('Reverse DNS (PTR)')}
                open={!!edit}
                onCancel={() => setEdit(null)}
                onOk={() => form.submit()}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={save}>
                    <Form.Item name="address" label={t('Address')} rules={[{ required: true }]} extra={edit?.prefix ? edit.prefix : undefined}>
                        <Input style={mono} disabled={!!edit?.address} placeholder={edit?.prefix ? edit.prefix.replace(/\/\d+$/, '1') : ''} />
                    </Form.Item>
                    <Form.Item name="ptr" label={t('Hostname')} rules={[{ required: true }]}>
                        <Input style={mono} placeholder="mail.example.com" />
                    </Form.Item>
                    {isAdmin && (
                        <Form.Item name="skip_check" valuePropName="checked" noStyle>
                            <Checkbox>{t('Skip the A / AAAA check')}</Checkbox>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Card>
    );
};

export default RdnsCard;
